import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { imageLibraryDisplayName } from '../../../queries/ImageLibrary';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { Button } from '../Button';
import { Input } from '../Input';
import { s } from './styles';

export interface ImageLibraryGridProps {
	items: Api.IImageLibraryItem[];
	isLoading: boolean;
	isError?: boolean;
	onRetry?: () => void;
	hasNextPage?: boolean;
	isFetchingNextPage?: boolean;
	onLoadMore?: () => void;
	onItemClick: (item: Api.IImageLibraryItem) => void;
	/** When provided, the caption becomes editable in place. Omit on surfaces that rename elsewhere. */
	onRename?: (item: Api.IImageLibraryItem, displayName: string) => void;
	selectedIds?: Set<string>;
	emptyTitle: string;
	emptyDescription: string;
}

function ImageCaption({
	item,
	onRename,
}: {
	item: Api.IImageLibraryItem;
	onRename?: (item: Api.IImageLibraryItem, displayName: string) => void;
}) {
	const currentName = imageLibraryDisplayName(item);
	const [isEditing, setIsEditing] = React.useState(false);
	const [draft, setDraft] = React.useState(currentName);

	if (!onRename) {
		return <div className={css(s.cardName)}>{currentName}</div>;
	}

	const commit = () => {
		const trimmed = draft.trim();
		setIsEditing(false);
		if (trimmed && trimmed !== currentName) {
			onRename(item, trimmed);
		} else {
			setDraft(currentName);
		}
	};

	if (isEditing) {
		return (
			<div className={css(s.cardNameRow)}>
				<Input
					autoFocus
					id={`image-library-name-${item.id}`}
					onBlur={commit}
					onChange={e => setDraft(e.target.value)}
					onKeyDown={e => {
						if (e.key === 'Enter') {
							commit();
						} else if (e.key === 'Escape') {
							setDraft(currentName);
							setIsEditing(false);
						}
					}}
					styles={[s.cardNameInput]}
					type='text'
					value={draft}
				/>
			</div>
		);
	}

	return (
		<Button
			onClick={() => {
				setDraft(currentName);
				setIsEditing(true);
			}}
			styles={[s.cardNameButton]}
			title='Rename'
			variant='ghost'
		>
			{currentName}
		</Button>
	);
}

export function ImageLibraryGrid({
	items,
	isLoading,
	isError,
	onRetry,
	hasNextPage,
	isFetchingNextPage,
	onLoadMore,
	onItemClick,
	onRename,
	selectedIds,
	emptyTitle,
	emptyDescription,
}: ImageLibraryGridProps) {
	if (isLoading) {
		return (
			<div className={css(s.loadingContainer)}>
				<LoadingSpinner />
			</div>
		);
	}

	// Without this an errored query looks identical to an empty library, which is a lie the user cannot act on.
	if (isError) {
		return (
			<div className={css(s.emptyState)}>
				<div className={css(s.emptyStateTitle)}>Could not load your images</div>
				<div className={css(s.emptyStateDescription)}>Something went wrong fetching the library.</div>
				{onRetry ? (
					<Button onClick={onRetry} variant='secondary'>
						Try again
					</Button>
				) : null}
			</div>
		);
	}

	if (!items.length) {
		return (
			<div className={css(s.emptyState)}>
				<div className={css(s.emptyStateTitle)}>{emptyTitle}</div>
				<div className={css(s.emptyStateDescription)}>{emptyDescription}</div>
			</div>
		);
	}

	return (
		<>
			<div className={css(s.grid)}>
				{items.map(item => (
					<div className={css(s.card, selectedIds?.has(item.id) && s.cardSelected)} key={item.id}>
						<Button
							aria-label={`Select ${imageLibraryDisplayName(item)}`}
							onClick={() => onItemClick(item)}
							styles={[s.cardImageButton]}
							variant='ghost'
						>
							<img alt={imageLibraryDisplayName(item)} className={css(s.cardImage)} loading='lazy' src={item.url} />
						</Button>
						<ImageCaption item={item} onRename={onRename} />
					</div>
				))}
			</div>
			{hasNextPage && !isFetchingNextPage && onLoadMore && (
				<Waypoint onEnter={onLoadMore}>
					<div className={css(s.loadMoreTrigger)}>
						<LoadingSpinner />
					</div>
				</Waypoint>
			)}
		</>
	);
}
